import React from 'react';
import { API } from '../api';

/*
 * Moving the logo and the QR code on a finished poster.
 *
 * The server measures the poster and puts them on its quietest patch, which
 * is right most of the time. It is measuring flatness, though, and flatness
 * is not the same as unimportance — the emptiest part of a picture is
 * occasionally the part the picture is about. There was no way to disagree
 * with it, and "no way to disagree" was most of what made adding a logo feel
 * like a gamble.
 *
 * So: the poster without its furniture, the furniture as a draggable block
 * over the top, and two numbers sent back. The picture is not composited
 * here. What is dragged is a preview — the real thing is redrawn by the same
 * server code that placed it the first time, so there is one account of how
 * wide a logo is and what colour its plate is, rather than one here and one
 * there that have to agree forever.
 *
 * Positions are fractions of the width and height, so nothing in this file
 * knows or cares that the poster is 4K.
 */

/* The same tenth of the height the server gives the furniture, so the block
   being dragged is the size of the thing it stands for. */
const PLATE_H = 0.10;

function PosterPlacer({ poster, onSaved, onClose, showToast }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [pos, setPos] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const frameRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const openerRef = React.useRef(null);
  /* Where in the block the pointer took hold. Without it the block jumps so
     its corner is under the cursor the moment you touch it, which reads as
     the thing moving on its own before you have dragged anything. */
  const grabRef = React.useRef({ dx: 0, dy: 0 });

  React.useEffect(() => {
    openerRef.current = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, [onClose]);

  React.useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/posters/${poster.id}/placement`)
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Could not open this poster.');
        return d;
      })
      .then(d => {
        if (cancelled) return;
        setData(d);
        const f = d.furniture;
        setPos(f ? { x: f.x, y: f.y, w: f.w, h: f.h } : { x: 0.03, y: 0.85, w: 0.3, h: PLATE_H });
      })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [poster.id]);

  /* Fractions of the frame, clamped so the block cannot be dragged off the
     poster — the server clamps too, but a block that visibly leaves the
     picture and silently comes back is a worse thing to watch. */
  const pointToPos = React.useCallback((clientX, clientY) => {
    const frame = frameRef.current;
    if (!frame || !pos) return null;
    const r = frame.getBoundingClientRect();
    const x = (clientX - r.left) / r.width - grabRef.current.dx;
    const y = (clientY - r.top) / r.height - grabRef.current.dy;
    return {
      ...pos,
      x: Math.min(1 - pos.w, Math.max(0, x)),
      y: Math.min(1 - pos.h, Math.max(0, y)),
    };
  }, [pos]);

  const onPointerDown = e => {
    const frame = frameRef.current;
    if (!frame || !pos) return;
    const r = frame.getBoundingClientRect();
    grabRef.current = {
      dx: (e.clientX - r.left) / r.width - pos.x,
      dy: (e.clientY - r.top) / r.height - pos.y,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    if (!dragging) return;
    const next = pointToPos(e.clientX, e.clientY);
    if (next) setPos(next);
  };

  const endDrag = e => {
    if (!dragging) return;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture && e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  /* Arrow keys move it too. A logo a few pixels off the edge is fiddly with a
     mouse and trivial with a key, and this is the only control on the screen,
     so somebody using a keyboard has no other way in at all. */
  const onKeyDown = e => {
    if (!pos) return;
    const step = e.shiftKey ? 0.05 : 0.005;
    const moves = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0],
      ArrowUp: [0, -step], ArrowDown: [0, step],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    setPos(p => ({
      ...p,
      x: Math.min(1 - p.w, Math.max(0, p.x + move[0])),
      y: Math.min(1 - p.h, Math.max(0, p.y + move[1])),
    }));
  };

  const save = async (auto = false) => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/posters/${poster.id}/placement`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(auto ? { auto: true } : { x: pos.x, y: pos.y }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'That did not work.'); return; }
      onSaved({ poster: d.poster });
      showToast(auto ? 'Put back where it was measured.' : 'Moved.');
      onClose();
    } catch (err) {
      setError(err.message || 'That did not work.');
    } finally {
      setSaving(false);
    }
  };

  const pct = n => `${n * 100}%`;

  return (
    <div className="poster-zoom" role="dialog" aria-modal="true"
      aria-label={`Move the logo on "${poster.title}"`}>
      <div className="poster-zoom-inner" onClick={e => e.stopPropagation()}>
        {error && !data
          ? <div className="poster-error" role="alert" style={{ margin: 24 }}>{error}</div>
          : null}

        {data && pos ? (
          <div className="poster-place-frame" ref={frameRef}>
            <img src={data.baseImage} alt="" className="poster-zoom-img" draggable={false} />

            {/* The block is the control, so it is the button: it takes focus,
                it takes the arrow keys, and it says where it is. */}
            <button
              type="button"
              className={`poster-place-block${dragging ? ' poster-place-block-on' : ''}`}
              style={{ left: pct(pos.x), top: pct(pos.y), width: pct(pos.w), height: pct(pos.h) }}
              aria-label={`Logo and code, ${Math.round(pos.x * 100)}% across and ${Math.round(pos.y * 100)}% down. Drag, or use the arrow keys.`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={onKeyDown}>
              {data.logo ? <img src={data.logo} alt="" draggable={false} /> : null}
              {data.qr ? <img src={data.qr} alt="" draggable={false} /> : null}
              {data.caption ? <span>{data.caption}</span> : null}
            </button>
          </div>
        ) : null}

        <div className="poster-zoom-bar">
          <div className="poster-zoom-title">
            {poster.title}
            <span className="poster-zoom-meta">
              {!data && !error ? 'opening…' : 'Drag the logo, or use the arrow keys.'}
            </span>
          </div>
          <div className="poster-actions">
            <button type="button" className="w3-button w3-round poster-btn-primary"
              disabled={!pos || saving} onClick={() => save(false)}>
              {saving ? 'Saving…' : 'Save where it is'}
            </button>
            {/* Always available, because the whole point of dragging is that
                you might drag it somewhere worse. */}
            <button type="button" className="w3-button w3-round poster-btn"
              disabled={!pos || saving} onClick={() => save(true)}>
              Put it back
            </button>
            <button type="button" className="w3-button w3-round poster-btn"
              ref={closeRef} disabled={saving} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

        {error && data ? <div className="poster-error" role="alert">{error}</div> : null}
      </div>
    </div>
  );
}

export default PosterPlacer;
